import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Users, Sparkles, MailCheck, Loader2, UserPlus, Download, Plus, Trash2 } from "lucide-react";

const STATUS_OPTIONS = ["new", "contacted", "converted", "archived"];
const STATUS_STYLES = {
  new: "text-accent border-accent/40 bg-accent/10",
  contacted: "text-primary border-primary/40 bg-primary/10",
  converted: "text-primary border-primary/50 bg-primary/15",
  archived: "text-muted-foreground border-border bg-secondary/40",
};
const EMPTY_FORM = { name: "", email: "", interest: "", source: "", status: "new" };

const STUDENT_STATUS = {
  not_signed_up: { label: "Never signed up", dot: "bg-rose-500", cls: "text-rose-300 border-rose-500/40 bg-rose-500/10" },
  signed_up: { label: "Signed up", dot: "bg-amber-400", cls: "text-amber-300 border-amber-400/40 bg-amber-400/10" },
  completed_lesson_1: { label: "Completed Lesson 1", dot: "bg-sky-400", cls: "text-sky-300 border-sky-400/40 bg-sky-400/10" },
  completed_golden_five: { label: "Golden Five ✓", dot: "bg-emerald-400", cls: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10" },
};

export default function AdminLeads() {
  const { user, ready } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    api.get("/admin/leads").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (ready && user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const setStatus = async (email, status) => {
    try {
      await api.patch(`/admin/leads/${encodeURIComponent(email)}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error("Could not update lead");
    }
  };

  const addContact = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/leads", {
        name: form.name.trim(),
        email: form.email.trim(),
        interest: form.interest.trim() || null,
        source: form.source.trim() || null,
        status: form.status,
      });
      toast.success("Contact added");
      setAddOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/leads/${encodeURIComponent(deleteTarget.email)}`);
      toast.success("Contact deleted");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dronacademy-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export leads");
    }
  };

  const stats = data
    ? [
        { label: "Total Leads", value: data.summary.total, icon: Users },
        { label: "New", value: data.summary.new, icon: UserPlus },
        { label: "Contacted", value: data.summary.contacted, icon: MailCheck },
        { label: "Converted", value: data.summary.converted, icon: Sparkles },
      ]
    : [];

  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <Users className="h-6 w-6 text-primary" />
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">Growth</span>
        </div>
        <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
          <h1 className="font-display text-3xl md:text-4xl font-bold">Contacts</h1>
          <div className="flex items-center gap-3">
            <Button onClick={() => { setForm(EMPTY_FORM); setAddOpen(true); }} className="rounded-sm" data-testid="add-contact-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Contact
            </Button>
            <Button onClick={exportCsv} variant="outline" disabled={!data || data.leads.length === 0}
              className="rounded-sm" data-testid="export-leads-csv">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-[40vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {stats.map((s, i) => (
                <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="p-5 rounded-sm border border-border/70 bg-card" data-testid={`lead-stat-${i}`}>
                  <s.icon className="h-5 w-5 text-primary mb-4" />
                  <div className="font-mono text-2xl font-bold">{s.value}</div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
                </motion.div>
              ))}
            </div>

            <div className="rounded-sm border border-border/70 bg-card overflow-hidden" data-testid="admin-leads-table">
              {data.showing < data.summary.total && (
                <div className="px-4 py-2 text-xs text-muted-foreground border-b border-border/50" data-testid="leads-showing-note">
                  Showing the {data.showing} most recent of {data.summary.total.toLocaleString()} contacts · use Export CSV for the full list.
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow className="border-border/60 hover:bg-transparent">
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Date</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Contact</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Interest</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Student</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Campaign</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Source</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Status</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.leads.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No contacts yet.</TableCell></TableRow>
                  ) : (
                    data.leads.map((l, i) => (
                      <TableRow key={l.email} className="border-border/50" data-testid={`admin-lead-${i}`}>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{l.name}</div>
                          <div className="text-xs text-muted-foreground">{l.email}</div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-400">{l.interest || "—"}</TableCell>
                        <TableCell>
                          {(() => {
                            const s = STUDENT_STATUS[l.student_status] || STUDENT_STATUS.not_signed_up;
                            return (
                              <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider border px-2 py-1 rounded-sm whitespace-nowrap ${s.cls}`} data-testid={`lead-student-status-${i}`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                                {s.label}
                                {l.student_paid && <span className="text-primary ml-0.5" title="Founder">· 💰 Founder</span>}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-sm">
                          {l.utm_campaign ? (
                            <span className="text-primary">{l.utm_campaign}</span>
                          ) : l.gclid ? (
                            <span className="text-accent font-mono text-xs">Google Ads</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {l.utm_source || l.source || "—"}
                          {l.utm_medium ? <span className="text-slate-500"> / {l.utm_medium}</span> : null}
                        </TableCell>
                        <TableCell>
                          <Select value={l.status} onValueChange={(v) => setStatus(l.email, v)}>
                            <SelectTrigger className={`w-[140px] h-8 text-xs font-mono uppercase tracking-wider border ${STATUS_STYLES[l.status] || STATUS_STYLES.new}`} data-testid={`lead-status-${i}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s} data-testid={`lead-status-opt-${s}`}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(l)}
                            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            data-testid={`delete-lead-${i}`} aria-label={`Delete ${l.email}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </main>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-contact-dialog">
          <DialogHeader>
            <DialogTitle>Add Contact</DialogTitle>
            <DialogDescription>Manually add a contact to your list. No notification email is sent.</DialogDescription>
          </DialogHeader>
          <form onSubmit={addContact} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="c-name">Name *</Label>
              <Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Pilot" data-testid="contact-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-email">Email *</Label>
              <Input id="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@example.com" data-testid="contact-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-interest">Interest</Label>
              <Input id="c-interest" value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}
                placeholder="Part 107 exam prep" data-testid="contact-interest-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="c-source">Source</Label>
                <Input id="c-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                  placeholder="referral" data-testid="contact-source-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="contact-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`contact-status-opt-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} data-testid="contact-cancel-btn">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="contact-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Contact"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-contact-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete contact?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="text-foreground font-medium">{deleteTarget?.name}</span> ({deleteTarget?.email}) from your contacts. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-confirm-btn">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
